import { Link, Locator, LocatorLocations, LocatorText, Publication, ReadingProgression } from '@readium/shared'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'

import { createAuthenticatedFetch } from '@/api/client'
import { useLatestRef } from '@/hooks/useLatestRef'

import {
  DEFAULT_READER_SETTINGS,
  getFontFamilyCSS,
  getThemeColors,
  type ReaderSettings,
} from '../../components/reader-settings-types'
import { clamp01, locateScroll, measureSections, scrollMaxOf, scrollPosOf } from './continuous-layout'
import { loadEpubPublication } from './epub-publication-loader'
import { EpubErrorDisplay, EpubLoadingOverlay } from './epub-shared-ui'
import type { LocationInfo, TextSelectionInfo } from './epub-viewer'
import type { EpubSearchResult } from './toc-utils'
import { searchPublication } from './toc-utils'

export type { EpubSearchResult } from './toc-utils'

/** 章（href）の中の位置（0〜1）を、スクロール量の比率（0〜1）にする。章が見つからなければ null */
export type ScrollRatioMeasure = (href: string, progression: number) => number | null

export type ContinuousViewerHandle = {
  goToHref: (href: string) => void
  goToLocator: (locator: Locator, textOffset?: number) => void
  search: (query: string) => Promise<EpubSearchResult[]>
  highlightSearchResults: (query: string, results: EpubSearchResult[]) => void
  clearSearchHighlights: () => void
  setActiveSearchHighlight: (result: EpubSearchResult | null) => void
  /** スクロール量の比率（0〜1）の位置へ移動する */
  scrollToRatio: (ratio: number) => void
}

type Props = {
  manifestUrl: string
  initialLocator?: Locator | null
  initialIsVertical?: boolean
  readerSettings?: ReaderSettings
  onClick?: () => void
  onTocLoaded?: (toc: Link[]) => void
  onPublicationReady?: (pub: Publication, positions: Locator[]) => void
  onLocationChange?: (location: LocationInfo) => void
  onTextSelected?: (selection: TextSelectionInfo) => void
  onSelectionCleared?: () => void
  onWritingModeDetected?: (isVertical: boolean) => void
  /** 本文の iframe が読み込まれたとき（流し読みの強調などを本文に適用する用） */
  onFrameLoaded?: (wnd: Window) => void
  /** 章の読み込みや画像で各章の長さが変わったとき（スクロール量と本文の位置の対応が変わる） */
  onLayoutChange?: (measure: ScrollRatioMeasure) => void
}

// --- Helpers ---

// 章の長さの変化を知らせる間隔 (ms)
const LAYOUT_NOTIFY_MS = 200

/** フラグメントを除いたhref同士の一致判定（完全一致 or サフィックス一致） */
const hrefMatchesSection = (href: string, sectionHref: string): boolean => {
  const a = href.split('#')[0]
  const b = sectionHref.split('#')[0]
  return a === b || a.endsWith(b) || b.endsWith(a)
}

/** セクションiframeに注入するテーマCSS */
const buildThemeCSS = (settings: ReaderSettings, isVertical: boolean): string => {
  const colors = getThemeColors(settings.theme)
  // 横書き: 左右パディング / 縦書き: 上下パディング
  const padding = isVertical
    ? `${settings.marginLeft}px 0 ${settings.marginRight}px 0`
    : `0 ${settings.marginRight}px 0 ${settings.marginLeft}px`

  // 縦書き: writing-mode設定 + 横方向に展開するため overflow-x/y と高さの制御を調整
  // 縦書き時は overflow-y: hidden を保持するため、共通の overflow: visible を使わない
  // box-sizing: border-box により padding が height 内に収まり、文字が見切れなくなる
  const verticalCSS = isVertical
    ? `html { writing-mode: vertical-rl !important; height: 100% !important; overflow-x: visible !important; overflow-y: hidden !important; box-sizing: border-box !important; }
       body { writing-mode: vertical-rl !important; height: 100% !important; width: auto !important; max-width: none !important; columns: auto !important; overflow-y: hidden !important; box-sizing: border-box !important; }`
    : 'html { height: auto !important; }'

  // 縦書き時は overflow-y を hidden に保つため、overflow: visible は横書き時のみ適用
  const overflowCSS = isVertical
    ? 'overflow-x: visible !important; overflow-y: hidden !important;'
    : 'overflow: visible !important;'

  // 本文中の要素に個別指定されたフォントも上書きする（original 時は本の指定を尊重）
  const fontFamily = getFontFamilyCSS(settings.fontFamily)
  const fontCSS = fontFamily ? `html, body, body * { font-family: ${fontFamily} !important; }` : ''

  return `
    ${verticalCSS}
    ${fontCSS}
    html, body {
      background-color: ${colors.backgroundColor} !important;
      color: ${colors.textColor} !important;
      font-size: ${settings.fontSize}px !important;
      line-height: ${settings.lineHeight} !important;
      margin: 0 !important;
      ${overflowCSS}
      max-width: none !important;
      width: auto !important;
    }
    body { padding: ${padding} !important; }
    a { color: ${colors.linkColor} !important; }
    ${
      isVertical
        ? `img, svg, video {
            max-height: 100% !important;
            max-width: 100vw !important;
            width: auto !important;
            height: auto !important;
            object-fit: contain !important;
          }`
        : `img, svg, video {
            max-width: 100% !important;
            height: auto !important;
            object-fit: contain !important;
          }`
    }
  `
}

// --- Component ---

export const ContinuousEpubViewer = forwardRef<ContinuousViewerHandle, Props>(function ContinuousEpubViewer(
  {
    manifestUrl,
    initialLocator,
    initialIsVertical = false,
    readerSettings,
    onClick,
    onTocLoaded,
    onPublicationReady,
    onLocationChange,
    onTextSelected,
    onSelectionCleared,
    onWritingModeDetected,
    onFrameLoaded,
    onLayoutChange,
  },
  ref,
) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [sections, setSections] = useState<Link[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isVertical, setIsVertical] = useState(initialIsVertical)

  const publicationRef = useRef<Publication | null>(null)
  const iframeMapRef = useRef<Map<number, HTMLIFrameElement>>(new Map())
  const blobUrlsRef = useRef<string[]>([])
  const loadedRef = useRef<Set<number>>(new Set())
  const isVerticalRef = useRef(initialIsVertical)
  const settingsRef = useRef(readerSettings)
  const sectionsRef = useRef<Link[]>([])
  const searchHighlightRef = useRef<{ query: string; results: EpubSearchResult[] } | null>(null)
  const activeHighlightRef = useRef<EpubSearchResult | null>(null)

  // コールバックをrefに保持
  const onClickRef = useLatestRef(onClick)
  const onLocationChangeRef = useLatestRef(onLocationChange)
  const onTextSelectedRef = useLatestRef(onTextSelected)
  const onSelectionClearedRef = useLatestRef(onSelectionCleared)
  const onWritingModeDetectedRef = useLatestRef(onWritingModeDetected)
  const onTocLoadedRef = useLatestRef(onTocLoaded)
  const onPublicationReadyRef = useLatestRef(onPublicationReady)
  const onFrameLoadedRef = useLatestRef(onFrameLoaded)
  const onLayoutChangeRef = useLatestRef(onLayoutChange)
  const layoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    settingsRef.current = readerSettings
  }, [readerSettings])

  const authFetch = useMemo(() => createAuthenticatedFetch(), [])

  // --- Navigation handle ---
  const scrollToSection = useCallback((index: number) => {
    const iframe = iframeMapRef.current.get(index)
    if (!iframe) return
    iframe.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }, [])

  /** iframe内のlocator位置までスクロールする（progression + テキスト検索フォールバック） */
  const scrollToLocatorInIframe = useCallback((iframe: HTMLIFrameElement, locator: Locator, textOffset?: number) => {
    const doc = iframe.contentDocument
    if (!doc?.body) {
      iframe.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
      return
    }

    const container = containerRef.current
    if (!container) {
      iframe.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
      return
    }

    const isVert = isVerticalRef.current

    // textOffset ベース: TreeWalker でテキストノードを走査し Range から正確な位置を取得
    if (textOffset != null && textOffset >= 0) {
      const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
      let accumulated = 0
      let node: Text | null = null

      while ((node = walker.nextNode() as Text | null)) {
        const nodeLen = node.textContent?.length ?? 0
        if (accumulated + nodeLen > textOffset) {
          const localOffset = textOffset - accumulated
          const range = doc.createRange()
          range.setStart(node, localOffset)
          range.setEnd(node, Math.min(localOffset + 1, nodeLen))
          const rect = range.getBoundingClientRect()

          if (isVert) {
            const targetX = iframe.offsetLeft + rect.left
            container.scrollTo({ left: targetX - container.clientWidth / 2, behavior: 'smooth' })
          } else {
            const targetY = iframe.offsetTop + rect.top
            container.scrollTo({ top: targetY - 80, behavior: 'smooth' })
          }
          return
        }
        accumulated += nodeLen
      }
    }

    // フォールバック: progression ベース
    const progression = locator.locations?.progression ?? -1
    if (progression >= 0) {
      if (isVert) {
        const totalWidth = doc.documentElement.scrollWidth
        const targetX = iframe.offsetLeft + totalWidth * (1 - progression)
        container.scrollTo({ left: targetX - container.clientWidth / 2, behavior: 'smooth' })
      } else {
        const totalHeight = doc.documentElement.scrollHeight
        const targetY = iframe.offsetTop + totalHeight * progression
        container.scrollTo({ top: targetY - 80, behavior: 'smooth' })
      }
      return
    }

    // フォールバック: セクション先頭にスクロール
    iframe.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' })
  }, [])

  /** iframe内の検索一致箇所に<mark>を注入 */
  const applySearchHighlightsToIframe = useCallback(
    (iframe: HTMLIFrameElement, sectionHref: string, query: string, results: EpubSearchResult[]) => {
      const doc = iframe.contentDocument
      if (!doc?.body) return

      // ハイライト用スタイルを注入
      let styleEl = doc.getElementById('cv-search-hl-style')
      if (!styleEl) {
        styleEl = doc.createElement('style')
        styleEl.id = 'cv-search-hl-style'
        doc.head.appendChild(styleEl)
      }
      styleEl.textContent = `
        mark[data-search-hl] { background-color: rgba(255, 255, 0, 0.4); color: inherit; padding: 0; }
        mark[data-search-hl][data-search-active] { background-color: rgba(255, 140, 0, 0.7); }
      `

      // このセクションに該当する結果をフィルタリング
      const sHref = sectionHref.split('#')[0]
      const matching = results.filter((r) => hrefMatchesSection(r.locator.href, sHref))
      if (matching.length === 0) return

      // オフセット降順でソート（後ろから処理して前のオフセットに影響しないようにする）
      const sorted = [...matching].sort((a, b) => b.textOffset - a.textOffset)

      for (const result of sorted) {
        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
        let accumulated = 0
        let node: Text | null = null

        while ((node = walker.nextNode() as Text | null)) {
          const nodeLen = node.textContent?.length ?? 0
          if (accumulated + nodeLen > result.textOffset) {
            const localStart = result.textOffset - accumulated
            const endInNode = Math.min(localStart + query.length, nodeLen)
            const range = doc.createRange()
            range.setStart(node, localStart)
            range.setEnd(node, endInNode)

            const mark = doc.createElement('mark')
            mark.setAttribute('data-search-hl', '')
            mark.setAttribute('data-text-offset', String(result.textOffset))

            try {
              range.surroundContents(mark)
            } catch {
              // cross-boundary: skip
            }
            break
          }
          accumulated += nodeLen
        }
      }
    },
    [],
  )

  /** iframe内の検索ハイライトをすべて除去 */
  const clearSearchHighlightsInIframe = useCallback((iframe: HTMLIFrameElement) => {
    const doc = iframe.contentDocument
    if (!doc) return

    const marks = doc.querySelectorAll('mark[data-search-hl]')
    marks.forEach((mark) => {
      const parent = mark.parentNode
      if (!parent) return
      while (mark.firstChild) {
        parent.insertBefore(mark.firstChild, mark)
      }
      parent.removeChild(mark)
    })
    doc.body?.normalize()
    doc.getElementById('cv-search-hl-style')?.remove()
  }, [])

  /** 全ロード済みiframeに検索ハイライトを注入 */
  const highlightSearchResults = useCallback(
    (query: string, results: EpubSearchResult[]) => {
      searchHighlightRef.current = { query, results }
      for (const [index, iframe] of iframeMapRef.current) {
        const section = sectionsRef.current[index]
        if (section) applySearchHighlightsToIframe(iframe, section.href, query, results)
      }
    },
    [applySearchHighlightsToIframe],
  )

  /** 全ロード済みiframeの検索ハイライトを除去 */
  const clearSearchHighlights = useCallback(() => {
    searchHighlightRef.current = null
    activeHighlightRef.current = null
    for (const [, iframe] of iframeMapRef.current) {
      clearSearchHighlightsInIframe(iframe)
    }
  }, [clearSearchHighlightsInIframe])

  /** アクティブな検索ハイライトを設定（他のハイライトとは異なる色） */
  const setActiveSearchHighlight = useCallback((result: EpubSearchResult | null) => {
    activeHighlightRef.current = result

    // 全iframeのactiveを解除
    for (const [, iframe] of iframeMapRef.current) {
      const doc = iframe.contentDocument
      if (!doc) continue
      doc.querySelectorAll('mark[data-search-active]').forEach((el) => {
        el.removeAttribute('data-search-active')
      })
    }

    if (!result) return

    // 対象セクションのiframeを特定してactiveを設定
    const sectionIdx = sectionsRef.current.findIndex((s) => hrefMatchesSection(result.locator.href, s.href))
    if (sectionIdx < 0) return

    const iframe = iframeMapRef.current.get(sectionIdx)
    const doc = iframe?.contentDocument
    if (!doc) return

    const mark = doc.querySelector(`mark[data-text-offset="${result.textOffset}"][data-search-hl]`)
    if (mark) mark.setAttribute('data-search-active', '')
  }, [])

  /** Publication全セクションをクライアント側で全文検索 */
  const searchInPublication = useCallback(async (query: string): Promise<EpubSearchResult[]> => {
    const pub = publicationRef.current
    if (!pub) return []

    return searchPublication(pub, query, async (link) => {
      // ロード済みiframeがあればDOMから直接テキスト取得
      const sectionIdx = sectionsRef.current.findIndex((s) => s.href === link.href)
      const iframe = sectionIdx >= 0 ? iframeMapRef.current.get(sectionIdx) : undefined
      if (iframe?.contentDocument?.body) {
        return iframe.contentDocument.body.textContent ?? ''
      }
      return null // フォールバック: Publication経由でHTMLを取得
    })
  }, [])

  useImperativeHandle(
    ref,
    () => ({
      goToHref: (href: string) => {
        const idx = sectionsRef.current.findIndex((s) => s.href === href || href.endsWith(s.href))
        if (idx >= 0) scrollToSection(idx)
      },
      search: searchInPublication,
      highlightSearchResults,
      clearSearchHighlights,
      setActiveSearchHighlight,
      goToLocator: (locator: Locator, textOffset?: number) => {
        // フラグメント(#id)を除いたパスで比較
        const idx = sectionsRef.current.findIndex((s) => hrefMatchesSection(locator.href, s.href))
        if (idx < 0) return

        // セクションが未ロードの場合はロードを待ってスクロール
        const iframe = iframeMapRef.current.get(idx)
        if (iframe) {
          scrollToLocatorInIframe(iframe, locator, textOffset)
        } else {
          // まだiframeが生成されていない→セクション先頭にスクロールして読み込みをトリガー
          scrollToSection(idx)
          // iframeが生成されるのを待ってスクロール
          const waitAndScroll = (retries: number) => {
            setTimeout(() => {
              const loaded = iframeMapRef.current.get(idx)
              if (loaded) {
                scrollToLocatorInIframe(loaded, locator, textOffset)
              } else if (retries > 0) {
                waitAndScroll(retries - 1)
              } else {
                scrollToSection(idx)
              }
            }, 300)
          }
          waitAndScroll(5)
        }
      },
      scrollToRatio: (ratio: number) => {
        const container = containerRef.current
        if (!container) return
        const vertical = isVerticalRef.current
        const target = clamp01(ratio) * scrollMaxOf(container, vertical)
        if (vertical) container.scrollTo({ left: -target })
        else container.scrollTo({ top: target })
      },
    }),
    [
      scrollToSection,
      scrollToLocatorInIframe,
      searchInPublication,
      highlightSearchResults,
      clearSearchHighlights,
      setActiveSearchHighlight,
    ],
  )

  // --- iframe auto-sizing ---
  const measureScrollRatio = useCallback<ScrollRatioMeasure>((href, progression) => {
    const container = containerRef.current
    const idx = sectionsRef.current.findIndex((s) => hrefMatchesSection(href, s.href))
    if (!container || idx < 0) return null
    const vertical = isVerticalRef.current
    const scrollMax = scrollMaxOf(container, vertical)
    const extent = measureSections(container, vertical).at(idx)
    if (!extent || scrollMax <= 0) return null
    return clamp01((extent.start + clamp01(progression) * extent.size) / scrollMax)
  }, [])

  const resizeIframe = useCallback(
    (iframe: HTMLIFrameElement, vertical?: boolean) => {
      const doc = iframe.contentDocument
      if (!doc) return
      const isVert = vertical ?? isVerticalRef.current

      if (isVert) {
        // 縦書き: 高さはコンテナのpx値に固定（paddingによる縦スクロールを防ぐ）
        const containerHeight = containerRef.current?.clientHeight ?? window.innerHeight
        iframe.style.height = `${containerHeight}px`
        iframe.style.width = `${doc.documentElement.scrollWidth}px`
      } else {
        // 横書き: 幅は100%、高さはコンテンツに合わせる
        iframe.style.width = '100%'
        iframe.style.height = `${doc.documentElement.scrollHeight}px`
      }

      if (layoutTimerRef.current) return
      layoutTimerRef.current = setTimeout(() => {
        layoutTimerRef.current = null
        onLayoutChangeRef.current?.((href, progression) => measureScrollRatio(href, progression))
      }, LAYOUT_NOTIFY_MS)
    },
    [onLayoutChangeRef, measureScrollRatio],
  )

  useEffect(
    () => () => {
      if (layoutTimerRef.current) clearTimeout(layoutTimerRef.current)
    },
    [],
  )

  // --- iframe内のイベントリスナー ---
  const attachIframeListeners = useCallback(
    (iframe: HTMLIFrameElement, sectionIndex: number) => {
      const doc = iframe.contentDocument
      if (!doc) return

      doc.addEventListener('click', (e: MouseEvent) => {
        if ((e.target as HTMLElement).closest?.('a')) return
        onClickRef.current?.()
      })

      doc.addEventListener('selectionchange', () => {
        const sel = doc.getSelection()
        if (!sel || sel.isCollapsed || !sel.toString().trim()) {
          onSelectionClearedRef.current?.()
          return
        }
        const text = sel.toString().trim()
        const link = sectionsRef.current[sectionIndex]
        if (!text || !link) return

        onTextSelectedRef.current?.({
          text,
          locator: new Locator({
            href: link.href,
            type: link.mediaType?.string ?? 'application/xhtml+xml',
            text: new LocatorText({ highlight: text }),
          }),
        })
      })
    },
    [onClickRef, onSelectionClearedRef, onTextSelectedRef],
  )

  // --- writing-mode検出 (最初のセクションから) ---
  const detectWritingMode = useCallback(
    (iframe: HTMLIFrameElement) => {
      const wnd = iframe.contentWindow
      const doc = iframe.contentDocument
      if (!wnd || !doc?.body) return

      // body と html の両方をチェック（EPUBによって設定箇所が異なる）
      const bodyWM = wnd.getComputedStyle(doc.body).writingMode
      const htmlWM = wnd.getComputedStyle(doc.documentElement).writingMode
      const vertical = bodyWM?.includes('vertical') || htmlWM?.includes('vertical') || false
      if (vertical === isVerticalRef.current) return
      isVerticalRef.current = vertical
      setIsVertical(vertical)
      onWritingModeDetectedRef.current?.(vertical)

      // コンテナのレイアウトを即座に切り替え（React再レンダリングを待たない）
      const container = containerRef.current
      if (container) {
        container.style.overflowY = vertical ? 'hidden' : 'auto'
        container.style.overflowX = vertical ? 'auto' : 'hidden'
        container.style.flexDirection = vertical ? 'row' : 'column'
        container.style.direction = vertical ? 'rtl' : 'ltr'

        // 各セクションラッパーのスタイルも更新
        container.querySelectorAll<HTMLElement>('[data-section-index]').forEach((el) => {
          el.style.minHeight = vertical ? '100%' : '100px'
          el.style.minWidth = vertical ? '100px' : '100%'
        })
      }

      // 全ロード済みiframeのCSSとサイズを更新
      const css = buildThemeCSS(settingsRef.current ?? DEFAULT_READER_SETTINGS, vertical)
      for (const [, frame] of iframeMapRef.current) {
        const fdoc = frame.contentDocument
        if (!fdoc) continue
        const style = fdoc.getElementById('cv-theme')
        if (style) style.textContent = css
        resizeIframe(frame, vertical)
      }
    },
    [resizeIframe, onWritingModeDetectedRef],
  )

  // --- セクション読み込み ---
  const loadSection = useCallback(
    async (index: number) => {
      const pub = publicationRef.current
      if (!pub || loadedRef.current.has(index)) return
      loadedRef.current.add(index)

      const link = sectionsRef.current[index]
      const iframe = iframeMapRef.current.get(index)
      if (!link || !iframe) return

      try {
        const html = await pub.get(link).readAsString()
        if (!html) return

        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        // base href: 相対リソース（画像・CSS）の解決用
        const baseURL = pub.manifest.baseURL ?? manifestUrl.replace(/\/[^/]*$/, '/')
        const sectionFullUrl = new URL(link.href, baseURL).href
        const sectionDirUrl = sectionFullUrl.replace(/[^/]*$/, '')
        const baseTag = doc.createElement('base')
        baseTag.href = sectionDirUrl
        doc.head.prepend(baseTag)

        // テーマCSS注入
        const style = doc.createElement('style')
        style.id = 'cv-theme'
        style.textContent = buildThemeCSS(settingsRef.current ?? DEFAULT_READER_SETTINGS, isVerticalRef.current)
        doc.head.appendChild(style)

        // Blob URL → iframe
        const mediaType = link.mediaType?.string ?? 'text/html'
        const blob = new Blob([new XMLSerializer().serializeToString(doc)], { type: mediaType })
        const blobUrl = URL.createObjectURL(blob)
        blobUrlsRef.current.push(blobUrl)

        iframe.onload = () => {
          resizeIframe(iframe)
          attachIframeListeners(iframe, index)
          if (iframe.contentWindow) onFrameLoadedRef.current?.(iframe.contentWindow)
          if (index === 0) {
            detectWritingMode(iframe)
            // CSS が遅延ロードされる場合に備え、段階的に再検出
            setTimeout(() => detectWritingMode(iframe), 200)
            setTimeout(() => detectWritingMode(iframe), 500)
            setTimeout(() => detectWritingMode(iframe), 1000)
          }

          // 画像等の遅延読み込みに対応するResizeObserver
          const body = iframe.contentDocument?.body
          if (body) {
            new ResizeObserver(() => resizeIframe(iframe)).observe(body)
          }

          // 検索ハイライトが有効なら新しくロードされたiframeにも適用
          const searchState = searchHighlightRef.current
          if (searchState) {
            const section = sectionsRef.current[index]
            if (section) {
              applySearchHighlightsToIframe(iframe, section.href, searchState.query, searchState.results)
            }
            const active = activeHighlightRef.current
            if (active) {
              const iframeDoc = iframe.contentDocument
              if (iframeDoc) {
                const mark = iframeDoc.querySelector(`mark[data-text-offset="${active.textOffset}"][data-search-hl]`)
                if (mark) mark.setAttribute('data-search-active', '')
              }
            }
          }
        }
        iframe.src = blobUrl
      } catch (_err) {
        // セクションの読み込み失敗はスキップ（他のセクションは継続読み込み）
      }
    },
    [
      manifestUrl,
      resizeIframe,
      attachIframeListeners,
      detectWritingMode,
      applySearchHighlightsToIframe,
      onFrameLoadedRef,
    ],
  )

  // --- 初期化: マニフェスト取得・Publication構築 ---
  useEffect(() => {
    if (!manifestUrl) return

    let destroyed = false

    const init = async () => {
      try {
        setIsLoading(true)
        setError(null)

        const { publication: pub, positions } = await loadEpubPublication(authFetch, manifestUrl)
        if (destroyed) return

        // TOC
        if (pub.toc?.items.length) onTocLoadedRef.current?.(pub.toc.items)

        publicationRef.current = pub
        const readingOrder = pub.readingOrder.items
        sectionsRef.current = readingOrder

        // Publication メタデータから縦書き検出（CSSに依存しないフォールバック）
        const progression = pub.metadata.effectiveReadingProgression
        if (progression === ReadingProgression.rtl && !isVerticalRef.current) {
          isVerticalRef.current = true
          setIsVertical(true)
          onWritingModeDetectedRef.current?.(true)
        }

        setSections(readingOrder)
        setIsLoading(false)

        onPublicationReadyRef.current?.(pub, positions)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error')
        setIsLoading(false)
      }
    }

    init()
    const blobUrls = blobUrlsRef.current
    const loaded = loadedRef.current
    return () => {
      destroyed = true
      blobUrls.forEach(URL.revokeObjectURL)
      blobUrlsRef.current = []
      loaded.clear()
    }
  }, [manifestUrl, authFetch, onPublicationReadyRef, onTocLoadedRef, onWritingModeDetectedRef])

  // --- IntersectionObserver: lazy loading ---
  useEffect(() => {
    const container = containerRef.current
    if (!container || sections.length === 0 || !publicationRef.current) return

    const PRELOAD_RANGE = 5
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const idx = Number((entry.target as HTMLElement).dataset.sectionIndex)
          if (isNaN(idx)) continue
          loadSection(idx)
          // 前後5セクションを先読み
          for (let offset = 1; offset <= PRELOAD_RANGE; offset++) {
            if (idx - offset >= 0) loadSection(idx - offset)
            if (idx + offset < sections.length) loadSection(idx + offset)
          }
        }
      },
      { root: container, rootMargin: '1500px' },
    )

    container.querySelectorAll<HTMLElement>('[data-section-index]').forEach((el) => {
      observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sections, loadSection])

  // --- スクロール位置 → 進捗 ---
  useEffect(() => {
    const container = containerRef.current
    if (!container || sections.length === 0) return

    const handleScroll = () => {
      const vertical = isVerticalRef.current
      const scrollPos = scrollPosOf(container, vertical)
      const scrollMax = scrollMaxOf(container, vertical)
      if (scrollMax <= 0) return

      const percentage = Math.min(scrollPos / scrollMax, 1)
      // 画面の先頭側の端にある章と、その章の中の進み具合（実際のレイアウトから求める）
      const { index, progression } = locateScroll(measureSections(container, vertical), scrollPos)
      const link = sections.at(index)
      if (!link) return

      onLocationChangeRef.current?.({
        locator: new Locator({
          href: link.href,
          type: link.mediaType?.string ?? 'application/xhtml+xml',
          locations: new LocatorLocations({ progression, totalProgression: percentage }),
        }),
        percentage,
        atEnd: percentage >= 0.99,
      })
    }

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [sections, onLocationChangeRef])

  // --- 設定変更 → 全ロード済みiframeのCSS更新 ---
  useEffect(() => {
    if (!readerSettings) return
    const css = buildThemeCSS(readerSettings, isVerticalRef.current)

    for (const [, iframe] of iframeMapRef.current) {
      const doc = iframe.contentDocument
      if (!doc) continue
      const style = doc.getElementById('cv-theme')
      if (style) style.textContent = css
      // CSS変更後にリサイズ
      requestAnimationFrame(() => resizeIframe(iframe))
    }
  }, [readerSettings, resizeIframe])

  // --- 初期位置への復帰 ---
  useEffect(() => {
    if (!initialLocator || sections.length === 0 || isLoading) return
    const idx = sections.findIndex((s) => s.href === initialLocator.href || initialLocator.href.endsWith(s.href))
    if (idx >= 0) {
      setTimeout(() => scrollToSection(idx), 1000)
    }
  }, [initialLocator, sections, isLoading, scrollToSection])

  // --- Render ---
  if (error) {
    return <EpubErrorDisplay error={error} />
  }

  const bg = readerSettings ? getThemeColors(readerSettings.theme).backgroundColor : '#1a1a1a'

  return (
    <>
      <EpubLoadingOverlay isLoading={isLoading} />
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{
          backgroundColor: bg,
          overflowY: isVertical ? 'hidden' : 'auto',
          overflowX: isVertical ? 'auto' : 'hidden',
          display: 'flex',
          flexDirection: isVertical ? 'row' : 'column',
          direction: isVertical ? 'rtl' : 'ltr',
        }}
      >
        {sections.map((link, index) => (
          <div
            key={link.href}
            data-section-index={index}
            style={{
              minHeight: isVertical ? '100%' : 100,
              minWidth: isVertical ? 100 : '100%',
              flexShrink: 0,
              direction: 'ltr',
            }}
          >
            <iframe
              ref={(el) => {
                if (el) iframeMapRef.current.set(index, el)
                else iframeMapRef.current.delete(index)
              }}
              title={`Section ${index}`}
              style={{
                border: 'none',
                display: 'block',
                width: isVertical ? undefined : '100%',
                height: isVertical ? '100%' : undefined,
                flexShrink: 0,
              }}
            />
          </div>
        ))}
      </div>
    </>
  )
})
